import { z } from "zod";
import { accessLineupRowSchema, accessLineupSchema } from "./access-lineup";
import { eventAccessUpsertSchema } from "./schema";

/** Stable refusals shared by manager RPCs and host-agnostic editors. */
export const managerAccessErrors = {
  forbiddenField: "manager_access_field_forbidden",
  eventRequired: "manager_access_event_required",
  engineRequired: "manager_access_engine_required",
  offerRequired: "manager_access_offer_required",
  quotaBelowSold: "quota_below_sold",
  currencyHasSales: "currency_locked_after_sale",
  // The engine refusal code, passed through unchanged (LIS-924).
  invalidOrderLimits: "invalid_order_limits",
  // A cancelled event can no longer be edited or published.
  cancelled: "manager_access_cancelled",
} as const;

const managerLineupSchema = z
  .array(
    z.discriminatedUnion("type", [
      z.strictObject(
        accessLineupRowSchema.options[0].omit({ collaborator: true }).shape,
        { error: managerAccessErrors.forbiddenField },
      ),
      z.strictObject(accessLineupRowSchema.options[1].shape, {
        error: managerAccessErrors.forbiddenField,
      }),
    ]),
  )
  .pipe(accessLineupSchema);

export const managerAccessUpsertSchema = z
  .strictObject(
    {
      ...eventAccessUpsertSchema.pick({
        accessId: true,
        artistId: true,
        title: true,
        description: true,
        instructions: true,
        instructionsImage: true,
        coverImageUrl: true,
        venue: true,
        address: true,
        locationFirst: true,
        buyerNameRule: true,
        buyerPhoneRule: true,
        city: true,
        country: true,
        startDateTime: true,
        endDateTime: true,
        doorsDateTime: true,
      }).shape,
      accessId: eventAccessUpsertSchema.shape.accessId.describe(
        "The event's accessId from manager.accesses.list to edit it, or a new random UUID to create one.",
      ),
      artistId: eventAccessUpsertSchema.shape.artistId.describe(
        "The workspace's artistId from manager.artists.list; the workspace must headline the event (lineup position 1).",
      ),
      lineup: managerLineupSchema
        .optional()
        .describe(
          "The acts in billing order, replacing the saved lineup: position 1 is the workspace itself, then supporting acts. Omitted keeps the saved lineup.",
        ),
      type: z
        .literal("event", { error: managerAccessErrors.eventRequired })
        .describe('Always "event".'),
      timezone: z
        .string()
        .min(1)
        .refine((zone) => {
          try {
            new Intl.DateTimeFormat("en", { timeZone: zone });
            return true;
          } catch {
            return false;
          }
        }, "Choose a valid IANA timezone")
        .describe(
          "The venue's IANA timezone, such as Europe/London, in which fans see the event's times.",
        ),
    },
    { error: managerAccessErrors.forbiddenField },
  )
  .refine(
    (event) =>
      !event.endDateTime ||
      Date.parse(event.endDateTime) > Date.parse(event.startDateTime),
    {
      path: ["endDateTime"],
      message: "End must be after start",
    },
  )
  .refine(
    (event) =>
      !event.doorsDateTime ||
      Date.parse(event.doorsDateTime) <= Date.parse(event.startDateTime),
    { path: ["doorsDateTime"], message: "Doors must open at or before start" },
  );

/**
 * What Lissen bought on a vendor's site for fans of one access: the tickets
 * (or merch items) and their order totals, each order in the currency it
 * was bought in. An act on the lineup receives the purchases started from
 * its own live link (`host`); a concert's organiser receives all of them
 * (`organiser`). Merch has no organiser.
 */
export const managerSurfaceSalesSchema = z.object({
  sold: z.number().int(),
  /** What the orders came to, one total per currency they were bought in
   *  (an access can have several sellers, each selling in its own), the
   *  most sold first, in minor units of each. Nothing is converted. Absent
   *  from a server older than it. */
  totals: z
    .array(z.object({ currency: z.string(), grossMinor: z.number().int() }))
    .optional(),
  /** The first of `totals` (with nothing sold, 0 in the listing's
   *  currency), for readers older than them: alone, it leaves out what was
   *  bought in any other currency. */
  grossMinor: z.number().int(),
  currency: z.string(),
  scope: z.enum(["host", "organiser"]),
  /** Set for a demo listing (priced in a currency 1-tap does not buy in):
   *  fans see it and its purchase sheet, and nothing is bought. Why, in
   *  words. */
  demoReason: z.string().nullable().optional(),
});
export type ManagerSurfaceSales = z.infer<typeof managerSurfaceSalesSchema>;

/** Manager inventory deliberately excludes staff controls and source provenance. */
export const managerAccessListEntrySchema = z.object({
  accessId: z.string(),
  artistId: z.string().nullable(),
  title: z.string(),
  visibility: z.string().optional(),
  type: z.literal("event"),
  startDateTime: z.string().optional(),
  endDateTime: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  /** False when the source published only a date: `startDateTime` is then that
   *  date's UTC midnight, read in UTC whatever `timezone` says. */
  startTimeKnown: z.boolean().optional(),
  venue: z.string().nullable().optional(),
  /** The event's poster, for the dashboard's preview. */
  coverImageUrl: z.string().nullable().optional(),
  /** Set once the event is cancelled in ticketing. */
  cancelledAt: z.string().nullable().optional(),
  /**
   * False for a listed event (no engine event, or another headliner) that the
   * workspace is on the lineup of or organises. Nonpublic listed events are
   * returned only to a headliner allowed to author them (`editable`).
   */
  managed: z.boolean().optional(),
  /**
   * True for a listed event the workspace may still edit and cancel: a
   * concert sold elsewhere that it headlines and that is managed in Lissen
   * for Partners (one it added by its ticket link, or one staff allowed).
   */
  editable: z.boolean().optional(),
  /** A listed event's first public ticket link. */
  ticketUrl: z.string().nullable().optional(),
  /**
   * Set when Lissen buys a listed event's tickets on the vendor's site for
   * the buyer: the tickets bought that way and their order totals. An act on
   * the lineup receives the purchases started from its own live link
   * (`host`); the event's organiser receives all of them (`organiser`).
   */
  surface: managerSurfaceSalesSchema.nullable().optional(),
});
export type ManagerAccessListEntry = z.infer<
  typeof managerAccessListEntrySchema
>;

/**
 * A merch product on the workspace's lineup that Lissen buys on a vendor's
 * site for fans (1-tap), as Inventory · Merch lists it. Like a listed event,
 * it is read-only here: staff list it, the vendor sells it.
 */
export const managerMerchListEntrySchema = z.object({
  accessId: z.string(),
  artistId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  /** The product's page on the vendor's store. */
  productUrl: z.string().nullable(),
  /** The vendor's name ("Music Glue"); null once its listing is removed. */
  vendorName: z.string().nullable(),
  /** Fans can buy it with 1-tap now (a demo listing is shown, never
   *  bought: `surface.demoReason`). */
  live: z.boolean(),
  currency: z.string(),
  /** These prices are before the seller's checkout tax. */
  taxAdded: z.literal(true).optional(),
  /** Its sizes (or the product alone) as fans choose them, while live. */
  sizes: z.array(
    z.object({
      name: z.string(),
      priceMinor: z.number().int(),
      available: z.boolean(),
    }),
  ),
  /** Always `host`: the purchases started from the workspace's own live link. */
  surface: managerSurfaceSalesSchema,
});
export type ManagerMerchListEntry = z.infer<typeof managerMerchListEntrySchema>;
export type ManagerAccessUpsert = z.infer<typeof managerAccessUpsertSchema>;
